import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Typography, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { InsuranceIntro } from '@/types/generated';
import { getDocProps, getWrapperStyles, getSelectSectionStyles, getInputLabelStyles, getSelectStyles, getSelectBoxStyles, getSelectTextStyles, containerStyles } from './Insurance.styles';
import InsuranceList, { IInsuranceListProps } from './InsuranceList/InsuranceList';
import RichText, { RichTextContent } from '@shared-components/components/RichText/RichText';
import { STATE_CODES, IStateCodes } from '@shared-components/constants';
import { Maybe } from '@shared-components/types';
import { dataLayerConfig } from '@utils/dataLayerConfig';
export interface IInsuranceProps {
  /**
   * Specifies the title of the template.
   *
   * @type {Maybe<string>}
   * @memberof IInsuranceProps
   */
  title?: Maybe<string>;
  /**
   * Rich text content that displays insurance intro
   *
   * @memberof IInsuranceProps
   */
  intro?: Maybe<InsuranceIntro>;
}
interface IInsuranceIntroSection {
  intro?: Maybe<InsuranceIntro>;
}
const InsuranceIntroSection = ({
  intro
}: Readonly<IInsuranceIntroSection>) => {
  if (!intro) return null;
  return <Box data-testid="section_insurance_intro" gap={2} data-sentry-element="Box" data-sentry-component="InsuranceIntroSection" data-sentry-source-file="Insurance.tsx">
			<RichText content={intro?.json as RichTextContent} links={intro?.links} docProps={getDocProps} data-sentry-element="RichText" data-sentry-source-file="Insurance.tsx" />
		</Box>;
};
export default function Insurance({
  title,
  intro
}: Readonly<IInsuranceProps>) {
  const [selectedState, setSelectedState] = React.useState<string>('');
  const [expanded, setExpanded] = React.useState(false);
  const [insuranceNames, setInsuranceNames] = React.useState<IInsuranceListProps['insuranceList']>([]);
  const theme = useTheme();
  const isLargeDevice = useMediaQuery(theme.breakpoints.up('lg'));
  const isMediumDevice = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('sm'));
  let screenSize: 'lg' | 'md' | 'sm' = 'sm';
  if (isLargeDevice) {
    screenSize = 'lg';
  } else if (isMediumDevice) {
    screenSize = 'md';
  }
  if (!title) return null;
  const handleStateChange = (event: SelectChangeEvent) => {
    const state = event.target.value;
    setSelectedState(state);
    dataLayerConfig({
      event: 'module_click',
      module_name: 'Insurance',
      cta_status: '',
      link_text: state,
      link_url: globalThis?.location?.hostname
    });
    setInsuranceNames([]);
    setExpanded(false);
  };
  const showMore = isSmallDevice && insuranceNames.length >= 10;
  const expandedList = expanded ? insuranceNames : insuranceNames.slice(0, 9);
  const insuranceList = isSmallDevice ? expandedList : insuranceNames;
  const getStates = (statesArray: IStateCodes[]) => {
    return statesArray.map(stateObj => stateObj.state);
  };
  const allOperatingStates = getStates(STATE_CODES);
  return <Box sx={containerStyles} data-sentry-element="Box" data-sentry-component="Insurance" data-sentry-source-file="Insurance.tsx">
			<Box data-testid="section_insurance" display={'flex'} flexDirection={'column'} sx={getWrapperStyles(screenSize)} data-sentry-element="Box" data-sentry-source-file="Insurance.tsx">
				<Box display={'flex'} flexDirection={isSmallDevice ? 'column' : 'row'} justifyContent={'space-between'} alignContent={'center'} gap={2} pb={3} data-testid="section_insurance_header" data-sentry-element="Box" data-sentry-source-file="Insurance.tsx">
					{title && <Typography variant="header2">{title}</Typography>}
					{allOperatingStates.length !== 0 && <Box sx={getSelectBoxStyles(isSmallDevice)} gap={2}>
							<Box sx={getSelectSectionStyles(isSmallDevice)}>
								<Typography variant="bodyMediumSemiBold" sx={getSelectTextStyles} color="text.tertiary">
									Choose your state
								</Typography>
							</Box>
							<FormControl fullWidth>
								<InputLabel data-testid="section_insurance_selectlabel" sx={getInputLabelStyles}>
									State
								</InputLabel>
								<Select fullWidth sx={getSelectStyles} data-testid="section_insurance_select" value={selectedState} label="State" onChange={handleStateChange}>
									{allOperatingStates.map(state => {
                return <MenuItem value={state} key={state} data-testid="section_insurance_menu">
												<Typography variant="buttonLabel">{state}</Typography>
											</MenuItem>;
              })}
								</Select>
							</FormControl>
						</Box>}
				</Box>
				<InsuranceIntroSection intro={intro} data-sentry-element="InsuranceIntroSection" data-sentry-source-file="Insurance.tsx" />
				<InsuranceList insuranceList={insuranceList} expanded={expanded} showMore={showMore} setExpanded={setExpanded} data-sentry-element="InsuranceList" data-sentry-source-file="Insurance.tsx" />
			</Box>
		</Box>;
}